import React, { useState, useRef } from 'react';
import dashboardService from '../../services/dashboardService';

const DashboardVolumeChart = ({ trendData = [] }) => {
  const [chartMode, setChartMode] = useState('financials'); // 'financials' | 'growth'
  const [activeMetric, setActiveMetric] = useState('all'); // 'all', 'volume', 'cashback'
  const [activeGrowthMetric, setActiveGrowthMetric] = useState('all'); // 'all', 'shoppers', 'creators', 'merchants'
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const svgRef = useRef(null);

  const data = trendData.length > 0 ? trendData : [
    { date: '2026-08-08', volume: 0, cashback: 0, transactions: 0, newShoppers: 0, newCreators: 0, newMerchants: 0 },
    { date: '2026-09-07', volume: 0, cashback: 0, transactions: 0, newShoppers: 0, newCreators: 0, newMerchants: 0 },
  ];

  // SVG Dimension ViewBox
  const width = 640;
  const height = 260;
  const padding = { top: 25, right: 25, bottom: 40, left: 55 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Compute Scales based on chartMode
  const maxVolume = Math.max(...data.map(d => d.volume || 0), 100);
  const maxCashback = Math.max(...data.map(d => d.cashback || 0), 20);
  const financialMaxY = Math.max(maxVolume, maxCashback * 2);

  const maxShoppers = Math.max(...data.map(d => d.newShoppers || 0), 1);
  const maxCreators = Math.max(...data.map(d => d.newCreators || 0), 1);
  const maxMerchants = Math.max(...data.map(d => d.newMerchants || 0), 1);
  const rawGrowthMax = Math.max(maxShoppers, maxCreators, maxMerchants, 5);
  // Round up to nice integer ceiling
  const growthMaxY = Math.ceil(rawGrowthMax * 1.15);

  const isFinancial = chartMode === 'financials';
  const maxY = isFinancial ? financialMaxY : growthMaxY;

  const getX = index => {
    if (data.length <= 1) return padding.left + chartWidth / 2;
    return padding.left + (index / (data.length - 1)) * chartWidth;
  };

  const getY = val => {
    return padding.top + chartHeight - (val / maxY) * chartHeight;
  };

  // Financials SVG Paths
  const volumePoints = data.map((d, i) => `${getX(i)},${getY(d.volume || 0)}`).join(' ');
  const volumeAreaPoints = `${getX(0)},${getY(0)} ${volumePoints} ${getX(data.length - 1)},${getY(0)}`;

  const cashbackPoints = data.map((d, i) => `${getX(i)},${getY(d.cashback || 0)}`).join(' ');
  const cashbackAreaPoints = `${getX(0)},${getY(0)} ${cashbackPoints} ${getX(data.length - 1)},${getY(0)}`;

  // Growth SVG Paths
  const shopperPoints = data.map((d, i) => `${getX(i)},${getY(d.newShoppers || 0)}`).join(' ');
  const shopperAreaPoints = `${getX(0)},${getY(0)} ${shopperPoints} ${getX(data.length - 1)},${getY(0)}`;

  const creatorPoints = data.map((d, i) => `${getX(i)},${getY(d.newCreators || 0)}`).join(' ');
  const creatorAreaPoints = `${getX(0)},${getY(0)} ${creatorPoints} ${getX(data.length - 1)},${getY(0)}`;

  const merchantPoints = data.map((d, i) => `${getX(i)},${getY(d.newMerchants || 0)}`).join(' ');
  const merchantAreaPoints = `${getX(0)},${getY(0)} ${merchantPoints} ${getX(data.length - 1)},${getY(0)}`;

  // Grid steps (4 horizontal guide lines)
  const gridSteps = [0, 0.25, 0.5, 0.75, 1];

  // Mouse hover handler
  const handleMouseMove = e => {
    if (!svgRef.current || data.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const svgX = (mouseX / rect.width) * width;

    // Find nearest point
    let closestIndex = 0;
    let minDiff = Infinity;
    data.forEach((d, i) => {
      const ptX = getX(i);
      const diff = Math.abs(ptX - svgX);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    });

    const target = data[closestIndex];
    setHoveredPoint({
      data: target,
      index: closestIndex,
      x: getX(closestIndex),
      // Financial coordinates
      yVol: getY(target.volume || 0),
      yCash: getY(target.cashback || 0),
      // Growth coordinates
      yShop: getY(target.newShoppers || 0),
      yCreat: getY(target.newCreators || 0),
      yMerch: getY(target.newMerchants || 0),
    });
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-xs p-5 mb-6 transition-all'>
      {/* Header with Title and Mode Switching */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4'>
        <div>
          <div className='flex items-center gap-2'>
            <h3 className='text-base font-bold text-gray-900'>
              {isFinancial ? 'Daily Transaction Volume & Cashback' : 'Daily Ecosystem Onboarding'}
            </h3>
            <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${
              isFinancial ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
            }`}>
              {isFinancial ? 'Financials' : 'User Growth'}
            </span>
          </div>
          <p className='text-xs text-gray-500 mt-0.5'>
            {isFinancial
              ? 'Daily trends across sales volume and shopper rewards'
              : 'Daily new signups across shoppers, creators, and merchants'}
          </p>
        </div>

        {/* Action Controls: Mode Switcher + Filter Chips */}
        <div className='flex flex-wrap items-center gap-2'>
          {/* Primary View Switcher */}
          <div className='flex items-center bg-gray-100 p-0.5 rounded-lg text-xs font-semibold'>
            <button
              onClick={() => {
                setChartMode('financials');
                setHoveredPoint(null);
              }}
              className={`px-3 py-1.5 rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                isFinancial ? 'bg-white text-blue-600 shadow-2xs font-bold' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              Financials (€)
            </button>
            <button
              onClick={() => {
                setChartMode('growth');
                setHoveredPoint(null);
              }}
              className={`px-3 py-1.5 rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
                !isFinancial ? 'bg-white text-purple-600 shadow-2xs font-bold' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' />
              </svg>
              Growth (Users)
            </button>
          </div>

          {/* Sub-Metric Toggles */}
          {isFinancial ? (
            <div className='flex items-center gap-1 bg-gray-50 border border-gray-200 p-0.5 rounded-lg text-2xs font-semibold'>
              <button
                onClick={() => setActiveMetric('all')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeMetric === 'all' ? 'bg-white text-gray-900 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveMetric('volume')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeMetric === 'volume' ? 'bg-white text-blue-600 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                ● Volume
              </button>
              <button
                onClick={() => setActiveMetric('cashback')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeMetric === 'cashback' ? 'bg-white text-emerald-600 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                ● Cashback
              </button>
            </div>
          ) : (
            <div className='flex items-center gap-1 bg-gray-50 border border-gray-200 p-0.5 rounded-lg text-2xs font-semibold'>
              <button
                onClick={() => setActiveGrowthMetric('all')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeGrowthMetric === 'all' ? 'bg-white text-gray-900 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveGrowthMetric('shoppers')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeGrowthMetric === 'shoppers' ? 'bg-white text-indigo-600 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                ● Shoppers
              </button>
              <button
                onClick={() => setActiveGrowthMetric('creators')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeGrowthMetric === 'creators' ? 'bg-white text-purple-600 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                ● Creators
              </button>
              <button
                onClick={() => setActiveGrowthMetric('merchants')}
                className={`px-2 py-1 rounded transition-all cursor-pointer ${
                  activeGrowthMetric === 'merchants' ? 'bg-white text-emerald-600 shadow-2xs font-bold' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                ● Merchants
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SVG Chart Container */}
      <div className='relative w-full overflow-hidden select-none'>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className='w-full h-auto cursor-crosshair'
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            {/* Financial Gradients */}
            <linearGradient id='volumeGrad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#3B82F6' stopOpacity='0.35' />
              <stop offset='100%' stopColor='#3B82F6' stopOpacity='0.0' />
            </linearGradient>
            <linearGradient id='cashbackGrad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#10B981' stopOpacity='0.35' />
              <stop offset='100%' stopColor='#10B981' stopOpacity='0.0' />
            </linearGradient>

            {/* Growth Gradients */}
            <linearGradient id='shopperGrad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#6366F1' stopOpacity='0.30' />
              <stop offset='100%' stopColor='#6366F1' stopOpacity='0.0' />
            </linearGradient>
            <linearGradient id='creatorGrad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#A855F7' stopOpacity='0.30' />
              <stop offset='100%' stopColor='#A855F7' stopOpacity='0.0' />
            </linearGradient>
            <linearGradient id='merchantGrad' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor='#10B981' stopOpacity='0.30' />
              <stop offset='100%' stopColor='#10B981' stopOpacity='0.0' />
            </linearGradient>
          </defs>

          {/* Grid lines & Y-axis labels */}
          {gridSteps.map((step, i) => {
            const yPos = padding.top + chartHeight * (1 - step);
            const val = maxY * step;
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={yPos}
                  x2={width - padding.right}
                  y2={yPos}
                  stroke='#E5E7EB'
                  strokeDasharray='3 3'
                  strokeWidth='1'
                />
                <text
                  x={padding.left - 8}
                  y={yPos + 4}
                  textAnchor='end'
                  fontSize='10'
                  fill='#9CA3AF'
                  fontFamily='monospace'
                >
                  {isFinancial
                    ? `€${dashboardService.formatNumber(val, true)}`
                    : dashboardService.formatNumber(Math.round(val), true)}
                </text>
              </g>
            );
          })}

          {/* ================= Financials Mode Curves ================= */}
          {isFinancial && (
            <>
              {(activeMetric === 'all' || activeMetric === 'volume') && (
                <>
                  <polygon points={volumeAreaPoints} fill='url(#volumeGrad)' />
                  <polyline
                    points={volumePoints}
                    fill='none'
                    stroke='#3B82F6'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </>
              )}
              {(activeMetric === 'all' || activeMetric === 'cashback') && (
                <>
                  <polygon points={cashbackAreaPoints} fill='url(#cashbackGrad)' />
                  <polyline
                    points={cashbackPoints}
                    fill='none'
                    stroke='#10B981'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </>
              )}
            </>
          )}

          {/* ================= Growth Mode Curves ================= */}
          {!isFinancial && (
            <>
              {(activeGrowthMetric === 'all' || activeGrowthMetric === 'shoppers') && (
                <>
                  <polygon points={shopperAreaPoints} fill='url(#shopperGrad)' />
                  <polyline
                    points={shopperPoints}
                    fill='none'
                    stroke='#6366F1'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </>
              )}
              {(activeGrowthMetric === 'all' || activeGrowthMetric === 'creators') && (
                <>
                  <polygon points={creatorAreaPoints} fill='url(#creatorGrad)' />
                  <polyline
                    points={creatorPoints}
                    fill='none'
                    stroke='#A855F7'
                    strokeWidth='2.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </>
              )}
              {(activeGrowthMetric === 'all' || activeGrowthMetric === 'merchants') && (
                <>
                  <polygon points={merchantAreaPoints} fill='url(#merchantGrad)' />
                  <polyline
                    points={merchantPoints}
                    fill='none'
                    stroke='#10B981'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </>
              )}
            </>
          )}

          {/* X Axis Date Labels */}
          {data.map((d, i) => {
            const step = Math.max(Math.floor(data.length / 5), 1);
            if (i % step !== 0 && i !== data.length - 1) return null;
            const xPos = getX(i);
            const dateStr = d.date ? d.date.substring(5) : '';
            return (
              <text
                key={i}
                x={xPos}
                y={height - 10}
                textAnchor='middle'
                fontSize='10'
                fill='#6B7280'
                fontWeight='500'
              >
                {dateStr}
              </text>
            );
          })}

          {/* Active Hover Crosshair & Dots */}
          {hoveredPoint && (
            <g>
              <line
                x1={hoveredPoint.x}
                y1={padding.top}
                x2={hoveredPoint.x}
                y2={padding.top + chartHeight}
                stroke='#94A3B8'
                strokeDasharray='4 4'
                strokeWidth='1.5'
              />

              {isFinancial && (
                <>
                  {(activeMetric === 'all' || activeMetric === 'volume') && (
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yVol}
                      r='5'
                      fill='#3B82F6'
                      stroke='#FFFFFF'
                      strokeWidth='2'
                      className='drop-shadow-sm'
                    />
                  )}
                  {(activeMetric === 'all' || activeMetric === 'cashback') && (
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yCash}
                      r='4'
                      fill='#10B981'
                      stroke='#FFFFFF'
                      strokeWidth='2'
                      className='drop-shadow-sm'
                    />
                  )}
                </>
              )}

              {!isFinancial && (
                <>
                  {(activeGrowthMetric === 'all' || activeGrowthMetric === 'shoppers') && (
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yShop}
                      r='5'
                      fill='#6366F1'
                      stroke='#FFFFFF'
                      strokeWidth='2'
                      className='drop-shadow-sm'
                    />
                  )}
                  {(activeGrowthMetric === 'all' || activeGrowthMetric === 'creators') && (
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yCreat}
                      r='4.5'
                      fill='#A855F7'
                      stroke='#FFFFFF'
                      strokeWidth='2'
                      className='drop-shadow-sm'
                    />
                  )}
                  {(activeGrowthMetric === 'all' || activeGrowthMetric === 'merchants') && (
                    <circle
                      cx={hoveredPoint.x}
                      cy={hoveredPoint.yMerch}
                      r='4'
                      fill='#10B981'
                      stroke='#FFFFFF'
                      strokeWidth='2'
                      className='drop-shadow-sm'
                    />
                  )}
                </>
              )}
            </g>
          )}
        </svg>

        {/* Floating Tooltip Card */}
        {hoveredPoint && (
          <div
            className='absolute pointer-events-none z-20 bg-gray-900/90 backdrop-blur-xs text-white p-3 rounded-lg shadow-lg border border-gray-700 text-xs transform -translate-x-1/2 -translate-y-full transition-all'
            style={{
              left: `${(hoveredPoint.x / width) * 100}%`,
              top: `${(isFinancial
                ? Math.min(hoveredPoint.yVol, hoveredPoint.yCash)
                : Math.min(hoveredPoint.yShop, hoveredPoint.yCreat, hoveredPoint.yMerch)) - 10}px`,
            }}
          >
            <div className='font-bold text-gray-200 border-b border-gray-700 pb-1 mb-1.5 flex items-center justify-between gap-4'>
              <span>{hoveredPoint.data.date}</span>
              <span className='text-2xs font-normal text-gray-400'>
                {isFinancial ? 'Transactions' : 'Onboarded'}
              </span>
            </div>

            {isFinancial ? (
              <div className='space-y-1 font-medium'>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-blue-400'>Volume:</span>
                  <span className='font-bold'>{dashboardService.formatCurrency(hoveredPoint.data.volume)}</span>
                </div>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-emerald-400'>Cashback:</span>
                  <span className='font-bold'>{dashboardService.formatCurrency(hoveredPoint.data.cashback)}</span>
                </div>
                <div className='flex items-center justify-between gap-4 text-gray-400 text-2xs pt-1 border-t border-gray-800'>
                  <span>Transactions:</span>
                  <span>{hoveredPoint.data.transactions || 0}</span>
                </div>
              </div>
            ) : (
              <div className='space-y-1 font-medium'>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-indigo-400'>Shoppers:</span>
                  <span className='font-bold'>+{hoveredPoint.data.newShoppers || 0}</span>
                </div>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-purple-400'>Creators:</span>
                  <span className='font-bold'>+{hoveredPoint.data.newCreators || 0}</span>
                </div>
                <div className='flex items-center justify-between gap-4'>
                  <span className='text-emerald-400'>Merchants:</span>
                  <span className='font-bold'>+{hoveredPoint.data.newMerchants || 0}</span>
                </div>
                <div className='flex items-center justify-between gap-4 text-gray-300 text-2xs pt-1 border-t border-gray-800 font-semibold'>
                  <span>Total Users:</span>
                  <span>
                    +{(hoveredPoint.data.newShoppers || 0) +
                      (hoveredPoint.data.newCreators || 0) +
                      (hoveredPoint.data.newMerchants || 0)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend Footer */}
      <div className='flex flex-wrap items-center justify-center gap-6 mt-2 pt-3 border-t border-gray-100 text-xs text-gray-600 font-medium'>
        {isFinancial ? (
          <>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-blue-500 inline-block'></span>
              <span>Gross Volume (€)</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-emerald-500 inline-block'></span>
              <span>Awarded Cashback (€)</span>
            </div>
          </>
        ) : (
          <>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-indigo-500 inline-block'></span>
              <span>New Shoppers</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-purple-500 inline-block'></span>
              <span>New Creators</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-emerald-500 inline-block'></span>
              <span>New Merchants</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardVolumeChart;
